import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { addNotification } from '../../store/slices/notificationSlice';
import Input from '../../components/ui/Input';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { setLoading, completeAuthSession } from '../../store/slices/authSlice';
import { AppDispatch } from '../../store/store';
import robotIllustration from '../../assets/img/Illustration-Login.png';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { useTranslation } from 'react-i18next';
import LoginLayout from '../../components/layout/LoginLayout';
import { AuthenticationService } from '../../api/generated/services/AuthenticationService';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    password?: string;
    confirmPassword?: string;
  }>({});
  
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Redirect if no token is provided
  useEffect(() => {
    if (!token) {
      dispatch(addNotification({
        type: 'error',
        title: t('notification_error'),
        message: t('reset_password_invalid_token'),
      }));
      navigate('/forgot-password');
    }
  }, [token, navigate, dispatch, t]);

  const validatePassword = useCallback((value: string) => {
    if (!value) {
      return t('validation_password_required');
    }
    if (value.length < 6) {
      return t('validation_password_short');
    }
    return '';
  }, [t]);

  const validateConfirmPassword = useCallback((value: string, originalPassword: string) => {
    if (!value) {
      return t('validation_password_required');
    }
    if (value !== originalPassword) {
      return t('validation_password_mismatch');
    }
    return '';
  }, [t]);

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    const error = validatePassword(value);
    setErrors(prev => ({
      ...prev,
      password: error,
      // Also re-validate confirm password if it's already filled
      confirmPassword: confirmPassword ? validateConfirmPassword(confirmPassword, value) : prev.confirmPassword
    }));
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    const error = validateConfirmPassword(value, password);
    setErrors(prev => ({
      ...prev,
      confirmPassword: error
    }));
  };

  const handlePasswordBlur = () => {
    setErrors(prev => ({
      ...prev,
      password: validatePassword(password)
    }));
  };

  const handleConfirmPasswordBlur = () => {
    setErrors(prev => ({
      ...prev,
      confirmPassword: validateConfirmPassword(confirmPassword, password)
    }));
  };

  const validateForm = () => {
    const newErrors = {
      password: validatePassword(password),
      confirmPassword: validateConfirmPassword(confirmPassword, password)
    };
    
    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!token) {
      dispatch(addNotification({
        type: 'error',
        title: t('notification_error'),
        message: t('reset_password_invalid_token'),
      }));
      return;
    }

    setIsSubmitting(true);
    dispatch(setLoading(true));
    
    try {
      const response = await AuthenticationService.postAppAuthResetPassword({
        token: token,
        password: password
      });

      // Centralized post-auth handshake (token persist, OpenAPI, bootstrap).
      await dispatch(completeAuthSession(response));

      dispatch(addNotification({
        type: 'success',
        title: t('notification_success'),
        message: t('reset_password_success'),
      }));
      navigate('/dashboard');
    } catch (error: any) {
      const errorMessage = error?.body?.error || error?.message || t('reset_password_error');
      dispatch(addNotification({
        type: 'error',
        title: t('notification_error'),
        message: errorMessage,
      }));
    } finally {
      setIsSubmitting(false);
      dispatch(setLoading(false));
    }
  };

  if (!token) {
    return null; // Will redirect in useEffect
  }

  return (
    <LoginLayout>
      <div className="login-illustration">
        <img src={robotIllustration} alt={t('resetPassword')} />
      </div>

      <Header isLoginHeader={true} showLoginHeader={true} />

      <div className="login-content">
        <div className="login-form-container">
          <div className="login-titles">
            <h1>{t('resetPassword')}</h1>
            <p>{t('reset_password_enter_new')}</p>
          </div>
          <form onSubmit={handleSubmit} className="login-form">
            <div className="input-group">
              <Input
                id="password"
                label={t('reset_password_new_password')}
                type="password"
                value={password}
                onChange={handlePasswordChange}
                onBlur={handlePasswordBlur}
                error={errors.password}
                required
              />
            </div>
            <div className="input-group">
              <Input
                id="confirmPassword"
                label={t('form_field_confirm_password')}
                type="password"
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                onBlur={handleConfirmPasswordBlur}
                error={errors.confirmPassword}
                required
              />
            </div>
            <button
              type="submit" 
              className="login-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? t('notification_loading') : t('resetPassword')}
            </button>
          </form>
          <Link to="/login" className="registration-link">{t('backToLogin')}</Link>
        </div>
      </div>
      <Footer />
    </LoginLayout>
  );
};

export default ResetPasswordPage;

